(() => {
  if (customElements.get('bbpd-diffuser-preorder')) return;

  class BbpdDiffuserPreorder extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.form = this.querySelector('[data-bbpd-product-form]');
      this.variantInput = this.querySelector('[data-bbpd-variant-id]');
      this.liveRegion = this.querySelector('[data-bbpd-live-region]');
      this.heroImage = this.querySelector('[data-bbpd-hero-image]');
      this.purchasePanel = this.querySelector('[data-bbpd-purchase-panel]');
      this.stickyBar = this.querySelector('[data-bbpd-sticky-bar]');
      this.stickyEnabled = this.dataset.stickyEnabled === 'true';

      this.addEventListener('change', this.onScentChange, { signal });
      this.addEventListener('click', this.onClick, { signal });
      this.form?.addEventListener('submit', this.onSubmit, { signal });

      const initial = this.querySelector('.bbpd-diffuser__radio:checked') || this.querySelector('.bbpd-diffuser__radio');
      if (initial) this.applyVariant(initial, { skipAnalytics: true, skipHistory: true });

      const urlVariant = new URLSearchParams(window.location.search).get('variant');
      if (urlVariant) {
        const match = this.querySelector(`.bbpd-diffuser__radio[value="${CSS.escape(urlVariant)}"]`);
        if (match && !match.checked) {
          match.checked = true;
          this.applyVariant(match, { skipAnalytics: true, skipHistory: true });
        }
      }

      if (this.stickyEnabled && this.purchasePanel && this.stickyBar && 'IntersectionObserver' in window) {
        this.stickyObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            const hide = !entry || entry.isIntersecting;
            this.stickyBar.hidden = hide;
            this.stickyBar.setAttribute('data-stuck', hide ? 'false' : 'true');
            this.classList.toggle('bbpd-diffuser--sticky-visible', !hide);
          },
          { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
        );
        this.stickyObserver.observe(this.purchasePanel);
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.stickyObserver?.disconnect();
    }

    onClick = (event) => {
      const scrollTrigger = event.target.closest('[data-bbpd-scroll-to]');
      if (scrollTrigger) {
        event.preventDefault();
        const target = this.querySelector(scrollTrigger.getAttribute('href'));
        if (!target) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        return;
      }

      const compact = event.target.closest('[data-bbpd-select-variant]');
      if (compact) {
        const variantId = compact.getAttribute('data-bbpd-select-variant');
        const radio = this.querySelector(`.bbpd-diffuser__radio[value="${CSS.escape(variantId)}"]`);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    };

    onScentChange = (event) => {
      const radio = event.target.closest('.bbpd-diffuser__radio');
      if (!radio) return;
      this.applyVariant(radio);
    };

    onSubmit = () => {
      const radio = this.querySelector('.bbpd-diffuser__radio:checked');
      if (!radio || radio.dataset.available !== 'true') return;
      this.pushEvent('bbpd_preorder_add_to_cart', radio);
    };

    plainPrice(value) {
      return String(value || '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
    }

    setHeroImage(radio) {
      const src = radio.dataset.heroSrc;
      if (!src) return;

      const media = this.querySelector('[data-bbpd-hero-media]');
      if (!media) return;

      let img = media.querySelector('img[data-bbpd-hero-image], img.bbpd-diffuser__hero-img');
      if (!img) {
        media.querySelectorAll('svg, img').forEach((node) => node.remove());
        img = document.createElement('img');
        img.className = 'bbpd-diffuser__hero-img';
        img.setAttribute('data-bbpd-hero-image', '');
        img.alt = radio.dataset.heroAlt || '';
        media.appendChild(img);
      }

      img.src = src;
      if (radio.dataset.heroSrcset) img.srcset = radio.dataset.heroSrcset;
      if (radio.dataset.heroAlt) img.alt = radio.dataset.heroAlt;
      this.heroImage = img;
    }

    applyVariant(radio, options = {}) {
      const variantId = radio.value;
      const available = radio.dataset.available === 'true';
      const title = radio.dataset.variantTitle || '';
      const scent = radio.closest('[data-scent]')?.getAttribute('data-scent');

      if (this.variantInput) this.variantInput.value = variantId;

      const heroMedia = this.querySelector('[data-bbpd-hero-media]');
      if (heroMedia && scent) heroMedia.setAttribute('data-scent', scent);

      this.querySelectorAll('[data-bbpd-price]').forEach((el) => {
        el.textContent = this.plainPrice(radio.dataset.priceFormatted);
      });

      this.querySelectorAll('[data-bbpd-compare]').forEach((el) => {
        const show = radio.dataset.showCompare === 'true';
        el.hidden = !show;
        if (!show) return;
        const valueEl = el.querySelector('[data-bbpd-compare-value]');
        const compare = this.plainPrice(radio.dataset.compareFormatted);
        if (valueEl) valueEl.textContent = compare;
        else el.textContent = compare;
      });

      this.querySelectorAll('[data-bbpd-cta-label], .bbpd-diffuser__submit .add-to-cart-text__content span span').forEach(
        (el) => {
          el.textContent = radio.dataset.ctaLabel || '';
        }
      );

      this.querySelectorAll('[data-bbpd-selected-scent]').forEach((el) => {
        el.textContent = title;
      });

      this.querySelectorAll('[data-bbpd-submit], .bbpd-diffuser__submit').forEach((button) => {
        button.disabled = !available;
      });
      const addToCartButton = this.querySelector('.bbpd-diffuser__purchase [ref="addToCartButton"], .bbpd-diffuser__purchase button[type="submit"]');
      if (addToCartButton) addToCartButton.disabled = !available;

      this.querySelectorAll('[data-bbpd-select-variant]').forEach((el) => {
        const isSelected = el.getAttribute('data-bbpd-select-variant') === variantId;
        el.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        el.classList.toggle('is-selected', isSelected);
      });

      this.setHeroImage(radio);

      const media = this.querySelector('add-to-cart-component');
      if (media && radio.dataset.heroSrc) media.setAttribute('data-product-variant-media', radio.dataset.heroSrc);

      if (this.liveRegion) {
        this.liveRegion.textContent = '';
        window.setTimeout(() => {
          this.liveRegion.textContent = this.dataset.selectedTemplate.replace('__SCENT__', title);
        }, 50);
      }

      if (!options.skipHistory && variantId && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      if (!options.skipAnalytics) this.pushEvent('bbpd_preorder_scent_select', radio);
    }

    pushEvent(eventName, radio) {
      try {
        window.dataLayer?.push({
          event: eventName,
          product: radio.dataset.productTitle || this.dataset.productTitle || 'BBPD Perfume Bottle Electric Diffuser',
          scent: radio.dataset.variantTitle,
          variant_id: Number(radio.value),
          value: Number(radio.dataset.price || 0) / 100,
          currency: window.Shopify?.currency?.active,
        });
      } catch (error) {
        /* Ignore analytics failures so checkout is never blocked. */
      }
    }
  }

  customElements.define('bbpd-diffuser-preorder', BbpdDiffuserPreorder);
})();
